import { useState } from "react";
import TableRow from "@mui/material/TableRow";
import IconButton from "@mui/material/IconButton";
import Collapse from "@mui/material/Collapse";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import TiktokAdGroupRow from "./TiktokAdGroupRow";
import { useFormContext } from "react-hook-form";
import { getFromStatisticsAdsCampaign } from "../../../../../services/adsCampaigns";
import { TIKTOK } from "../../../../../utils/constants";
import {
  VALUE_NUMBER,
  VALUE_WITH_CURRENCY_MANUAL_FORMAT,
  VALUE_WITH_PERCENTAGE,
} from "../../../../../i18n/keysTranslations";
import { useTranslationApp } from "../../../../../lib/i18next";
import TableCell from "../../../../BillingAndPayments/components/TableCell";
import Box from "@mui/material/Box";
import Avatar from "@mui/material/Avatar";
import { logoPlatform } from "../../../../../utils/string";
import Typography from "@mui/material/Typography";
import { renderMetricValue } from "../../../../../utils/renderMetricValue";

const TiktokCampaignRow = ({ index, sortConfig, columns }) => {
  const [open, setOpen] = useState(false);
  const { campaign } = useFormContext();
  const { statistics } = getFromStatisticsAdsCampaign(campaign);

  const { t } = useTranslationApp();

  const statisticsPlatform = statistics[TIKTOK].campaign;
  const adsGroups = campaign.adsGroups;
  const adsGroupsTiktok = adsGroups.filter(
    (adsGroup) => adsGroup.platform === TIKTOK
  );

  // Sort ad groups
  const sortedAdsGroups = [...adsGroupsTiktok].sort((a, b) => {
    const aStats = statistics[TIKTOK].adsGroups[a.id];
    const bStats = statistics[TIKTOK].adsGroups[b.id];

    if (!aStats || !bStats) return 0;

    if (sortConfig.key) {
      const comparison =
        (aStats[sortConfig.key] || 0) - (bStats[sortConfig.key] || 0);
      return sortConfig.direction === "asc" ? comparison : -comparison;
    }
    return 0;
  });

  const { getCostByMainCurrency } = useFormContext();

  const { cost, currency } = getCostByMainCurrency({
    cost: statisticsPlatform.spend,
    currency: statisticsPlatform.currency,
  });

  const { cost: costPerMainAction, currency: currencyPerMainAction } =
    getCostByMainCurrency({
      cost: statisticsPlatform.costPerMainAction,
      currency: statisticsPlatform.currency,
    });

  // Mapping to render each cell according to column
  const cellRenderers = {
    platform: () => (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: 1,
          height: "100%",
          width: "100%",
        }}
      >
        <Avatar
          data-hide-in-pdf-capture
          src={logoPlatform(TIKTOK)}
          variant="square"
          sx={{
            width: 20,
            height: 20,
            ".MuiAvatar-img": { objectFit: "contain" },
          }}
        />
        <Typography variant="body2" sx={{ mt: 0.25 }}>
          {t(TIKTOK)}
        </Typography>
      </Box>
    ),
    spend: () =>
      t(VALUE_WITH_CURRENCY_MANUAL_FORMAT, { value: cost, currency }),
    impressions: () =>
      t(VALUE_NUMBER, { value: statisticsPlatform.impressions }),
    click: () => t(VALUE_NUMBER, { value: statisticsPlatform.click }),
    ctr: () => t(VALUE_WITH_PERCENTAGE, { value: statisticsPlatform.ctr }),
    mainAction: () => t(VALUE_NUMBER, { value: statisticsPlatform.mainAction }),
    costPerMainAction: () =>
      renderMetricValue(
        costPerMainAction,
        "costPerMainAction",
        currencyPerMainAction
      ),
    cpc: () =>
      t(VALUE_WITH_CURRENCY_MANUAL_FORMAT, {
        value: (statisticsPlatform.cpc || 0).toFixed(2),
        currency: statisticsPlatform.currency,
      }),
    cpm: () =>
      t(VALUE_WITH_CURRENCY_MANUAL_FORMAT, {
        value: (statisticsPlatform.cpm || 0).toFixed(2),
        currency: statisticsPlatform.currency,
      }),
    // Here you can add custom renderers for specific metrics
  };

  return (
    <>
      <TableRow>
        <TableCell indexRow={index} sx={{ width: 25 }} level={1}>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        {columns.map((col) => (
          <TableCell
            key={col.key}
            indexRow={index}
            level={1}
            sx={{ width: 192, maxWidth: 192, minWidth: 192 }}
          >
            {cellRenderers[col.key]
              ? cellRenderers[col.key]()
              : renderMetricValue(
                  statisticsPlatform[col.key],
                  col.key,
                  statisticsPlatform.currency
                )}
          </TableCell>
        ))}
      </TableRow>
      <TableRow>
        <TableCell
          colSpan={columns.length + 1}
          indexRow={index}
          sx={{ p: 0, height: 0 }}
        >
          <Collapse in={open} timeout="auto" unmountOnExit>
            {sortedAdsGroups.map((adGroup, indexAdGroup) => {
              const adGroupID = adGroup.id;
              const adGroupStatistics = statistics[TIKTOK].adsGroups[adGroupID];

              if (!adGroupStatistics) return null;

              const adsStatistics = {};

              for (const ad of adGroup.ads) {
                const adID = ad.id;
                const adStatistics = statistics[TIKTOK].ads[adID];

                if (!adStatistics) continue;

                adsStatistics[adID] = adStatistics;
              }

              return (
                <TiktokAdGroupRow
                  indexCampaign={index}
                  adGroupID={adGroupID}
                  indexAdGroup={indexAdGroup}
                  key={adGroup.id}
                  data={adGroupStatistics}
                  adsStatistics={adsStatistics}
                  sortConfig={sortConfig}
                  columns={columns}
                />
              );
            })}
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export default TiktokCampaignRow;
